import { KleanadsAdUnit, KleanadsOutput } from '@pubstack/common/src/kleanadsOutput'
import { AdmStackOutput } from '@pubstack/common/src/stack'
import { isEqual } from 'lodash-es'
import { useMemo } from 'react'

type BidderChangelog = Record<string, { adUnitNames?: string[] }>

type DeviceChangelog = {
  adUnits: {
    added: string[]
    removed: string[]
    // "AdUnitName · Label: before → after" lines for fields of ad units present in both (excluding bidders and floors).
    changes: string[]
  }
  bidders: {
    added: BidderChangelog
    changed: BidderChangelog
    removed: BidderChangelog
  }
  // Prebid config (pbjsConfig fields + adCallTimeout): the labels of the fields that were added/changed/removed.
  prebidConfig: {
    added: string[]
    changed: string[]
    removed: string[]
  }
  // Human-readable "Label: before → after" lines describing what changed between snapshot and output.
  floors: { changes: string[] }
  refreshRules: { changes: string[] }
  lazyLoading: { changes: string[] }
  gamConfig: { changes: string[] }
}

export type AdmChangelog = {
  desktop: DeviceChangelog
  mobile: DeviceChangelog
}

type BidderMapping = { params: unknown; mediaTypes: string[] }

const getBiddersMap = (adUnits: KleanadsAdUnit[]) =>
  adUnits.reduce(
    (acc, adUnit) => {
      adUnit.bidders.forEach(({ bidder, params, adUnitMediaTypes }) => {
        acc[bidder] ||= {}
        // A bidder can appear several times on the same ad unit (one entry per media type); merge them so the
        // comparison is on the full media-type set + params, independent of how they're split across entries.
        const existing = acc[bidder][adUnit.adUnitName]
        acc[bidder][adUnit.adUnitName] = {
          params,
          mediaTypes: [...new Set([...(existing?.mediaTypes ?? []), ...(adUnitMediaTypes ?? [])])].sort(),
        }
      })
      return acc
    },
    {} as Record<string, Record<string, BidderMapping>>
  )

const getFloorsMap = (adUnits: KleanadsAdUnit[]) => Object.fromEntries(adUnits.map((a) => [a.adUnitName, a.floors]))

type RefreshSettings = { timer?: number; capping?: number | null; specificRules?: unknown }

const getRefreshMap = (output: KleanadsOutput): Record<string, RefreshSettings | undefined> => {
  const map: Record<string, RefreshSettings> = {}
  output.refreshConfig?.adUnitConfig.forEach((rule) =>
    rule.adUnitNames.forEach((adUnitName) => {
      map[adUnitName] = { timer: rule.timer, capping: rule.capping, specificRules: rule.specificRules }
    })
  )
  return map
}

const formatValue = (value: unknown): string => (value === undefined || value === null ? '—' : String(value))
const isScalar = (value: unknown): boolean => value === undefined || value === null || typeof value !== 'object'

// Emit a "Label: before → after" line per changed field. Complex (object/array) values have no readable
// scalar, so they fall back to a label-only line.
type FieldDef<T> = { label: string; get: (value: T | null | undefined) => unknown }
const diffFields = <T>(prefix: string, prev: T | null | undefined, curr: T | null | undefined, fields: FieldDef<T>[]): string[] =>
  fields.flatMap(({ label, get }) => {
    const before = get(prev)
    const after = get(curr)
    if (isEqual(before, after)) return []
    return [isScalar(before) && isScalar(after) ? `${prefix}${label}: ${formatValue(before)} → ${formatValue(after)}` : `${prefix}${label}`]
  })

const LAZY_LOADING_FIELDS: FieldDef<NonNullable<KleanadsOutput['lazyLoading']>>[] = [
  { label: 'Rule name', get: (l) => l?.configName },
  { label: 'Fetch margin', get: (l) => l?.lazyLoadingGoogle?.googleFetchMarginPercent },
  { label: 'Mobile scaling', get: (l) => l?.lazyLoadingGoogle?.googleMobileScaling },
  { label: 'Render margin', get: (l) => l?.lazyLoadingGoogle?.googleRenderMarginPercent },
  { label: 'Viewport', get: (l) => l?.lazyLoadingKleanads?.viewportPercent },
]

const GAM_CONFIG_FIELDS: FieldDef<NonNullable<KleanadsOutput['gamConfig']>>[] = [
  { label: 'Network ID', get: (g) => g?.networkId },
  { label: 'Ad unit path config', get: (g) => g?.adUnitPathConfig },
  { label: 'MCM IDs', get: (g) => g?.mcmIds },
]

const REFRESH_FIELDS: FieldDef<RefreshSettings>[] = [
  { label: 'Timer', get: (r) => r?.timer },
  { label: 'Capping', get: (r) => r?.capping },
  { label: 'Specific rules', get: (r) => r?.specificRules },
]

const FLOOR_FIELDS: FieldDef<NonNullable<KleanadsAdUnit['floors']>>[] = [
  { label: 'Currency', get: (f) => f?.currency },
  { label: 'Dynamic', get: (f) => f?.dynamic },
  { label: 'Floor prices', get: (f) => f?.values },
]

// Floors toggle on/off entirely, so report presence changes as a single line rather than per-field noise.
const diffFloor = (prefix: string, prev: KleanadsAdUnit['floors'], curr: KleanadsAdUnit['floors']): string[] => {
  if (isEqual(prev, curr)) return []
  if (!prev && curr) return [`${prefix}Floor: none → set`]
  if (prev && !curr) return [`${prefix}Floor: set → none`]
  return diffFields(prefix, prev, curr, FLOOR_FIELDS)
}

// Every ad unit field except bidders and floors (handled separately) and adUnitName (the key).
const AD_UNIT_FIELDS: FieldDef<KleanadsAdUnit>[] = [
  { label: 'Ad server ad unit name', get: (a) => a?.adServerAdUnitName },
  { label: 'Div ID', get: (a) => a?.divId },
  { label: 'Ad format', get: (a) => a?.adFormat },
  { label: 'Sizes', get: (a) => a?.sizes },
  { label: 'Extra ad server sizes', get: (a) => a?.extraAdServerSizes },
  { label: 'Media types', get: (a) => a?.mediaTypes },
  { label: 'Dynamic', get: (a) => a?.dynamic },
  { label: 'Parallax', get: (a) => a?.parallax },
  { label: 'Header bidding', get: (a) => a?.headerBiddingEnabled },
  { label: 'Can outstream', get: (a) => a?.canOutstream },
  { label: 'Is instream', get: (a) => a?.isInstream },
  { label: 'Can native', get: (a) => a?.canNative },
  { label: 'Anchor', get: (a) => a?.anchor },
  { label: 'Targeting', get: (a) => a?.targeting },
  { label: 'Extra targetings', get: (a) => a?.extraTargetings },
  { label: 'Out of page type', get: (a) => a?.outOfPageType },
  { label: 'Roadblock', get: (a) => a?.roadblock },
  { label: 'Native params', get: (a) => a?.nativeParams },
  { label: 'Instream params', get: (a) => a?.instreamParams },
]

// adCallTimeout + every KleanadsPbjsConfigSchema field, so the changelog reports any prebid config change.
const PREBID_CONFIG_FIELDS: FieldDef<KleanadsOutput['prebidConfig']>[] = [
  { label: 'Ad call timeout', get: (c) => c?.adCallTimeout },
  { label: 'APS account ID', get: (c) => c?.apsAccountId },
  { label: 'Analytics', get: (c) => c?.pbjsConfig.analytics },
  { label: 'Bid adjustments', get: (c) => c?.pbjsConfig.bidderAdjustments },
  { label: 'Bidder aliases', get: (c) => c?.pbjsConfig.bidderAliases },
  { label: 'Bidder configs', get: (c) => c?.pbjsConfig.bidderConfigs },
  { label: 'Currency', get: (c) => c?.pbjsConfig.currency },
  { label: 'Floors', get: (c) => c?.pbjsConfig.floors },
  { label: 'Price granularity', get: (c) => c?.pbjsConfig.priceGranularity },
  { label: 'Real time data', get: (c) => c?.pbjsConfig.realTimeData },
  { label: 'Supply chain', get: (c) => c?.pbjsConfig.schain },
  { label: 'Targeting behaviors', get: (c) => c?.pbjsConfig.targetingBehaviors },
  { label: 'User sync', get: (c) => c?.pbjsConfig.userSync },
  { label: 'Allow local storage', get: (c) => c?.pbjsConfig.allowLocalStorage },
  { label: 'Outbrain', get: (c) => c?.pbjsConfig.outbrain },
  { label: 'Enable TIDs', get: (c) => c?.pbjsConfig.enableTIDs },
  { label: 'Server-to-server', get: (c) => c?.pbjsConfig.s2sConfig },
]

const isFieldSet = (value: unknown): boolean => value !== undefined && value !== null

// Classify each prebid config field as added (none → set), removed (set → none) or changed (set → set, different).
const diffPrebidConfig = (prev: KleanadsOutput['prebidConfig'] | undefined, curr: KleanadsOutput['prebidConfig'] | undefined) => {
  const added: string[] = []
  const changed: string[] = []
  const removed: string[] = []
  PREBID_CONFIG_FIELDS.forEach(({ label, get }) => {
    const before = get(prev)
    const after = get(curr)
    if (isEqual(before, after)) return
    if (!isFieldSet(before)) added.push(label)
    else if (!isFieldSet(after)) removed.push(label)
    else changed.push(label)
  })
  return { added, changed, removed }
}

const computeDeviceChangelog = (prev: KleanadsOutput | undefined, curr: KleanadsOutput | undefined): DeviceChangelog => {
  const empty: DeviceChangelog = {
    adUnits: { added: [], removed: [], changes: [] },
    bidders: { added: {}, changed: {}, removed: {} },
    prebidConfig: { added: [], changed: [], removed: [] },
    floors: { changes: [] },
    refreshRules: { changes: [] },
    lazyLoading: { changes: [] },
    gamConfig: { changes: [] },
  }

  if (!prev || !curr) return empty

  const prevAdUnitNames = prev.adUnits.map((a) => a.adUnitName)
  const currAdUnitNames = curr.adUnits.map((a) => a.adUnitName)

  const prevBidders = getBiddersMap(prev.adUnits)
  const currBidders = getBiddersMap(curr.adUnits)
  const allBidders = new Set([...Object.keys(prevBidders), ...Object.keys(currBidders)])

  const added: BidderChangelog = {}
  const removed: BidderChangelog = {}
  const changed: BidderChangelog = {}

  allBidders.forEach((bidder) => {
    const prevUnits = Object.keys(prevBidders[bidder] || {})
    const currUnits = Object.keys(currBidders[bidder] || {})

    const addedUnits = currUnits.filter((u) => !prevUnits.includes(u))
    const removedUnits = prevUnits.filter((u) => !currUnits.includes(u))
    const changedUnits = currUnits.filter((u) => prevUnits.includes(u) && !isEqual(currBidders[bidder]?.[u], prevBidders[bidder]?.[u]))

    if (addedUnits.length > 0) added[bidder] = { adUnitNames: addedUnits }
    if (removedUnits.length > 0) removed[bidder] = { adUnitNames: removedUnits }
    if (changedUnits.length > 0) changed[bidder] = { adUnitNames: changedUnits }
  })

  const prevAdUnitsByName = Object.fromEntries(prev.adUnits.map((a) => [a.adUnitName, a]))
  const currAdUnitsByName = Object.fromEntries(curr.adUnits.map((a) => [a.adUnitName, a]))

  const prevFloorsMap = getFloorsMap(prev.adUnits)
  const currFloorsMap = getFloorsMap(curr.adUnits)
  const commonAdUnits = Object.keys(prevFloorsMap).filter((n) => n in currFloorsMap)

  const prevRefreshMap = getRefreshMap(prev)
  const currRefreshMap = getRefreshMap(curr)

  return {
    adUnits: {
      added: currAdUnitNames.filter((n) => !prevAdUnitNames.includes(n)),
      removed: prevAdUnitNames.filter((n) => !currAdUnitNames.includes(n)),
      changes: commonAdUnits.flatMap((n) => diffFields(`${n} · `, prevAdUnitsByName[n], currAdUnitsByName[n], AD_UNIT_FIELDS)),
    },
    bidders: { added, changed, removed },
    prebidConfig: diffPrebidConfig(prev.prebidConfig, curr.prebidConfig),
    floors: { changes: commonAdUnits.flatMap((n) => diffFloor(`${n} · `, prevFloorsMap[n], currFloorsMap[n])) },
    refreshRules: { changes: commonAdUnits.flatMap((n) => diffFields(`${n} · `, prevRefreshMap[n], currRefreshMap[n], REFRESH_FIELDS)) },
    lazyLoading: { changes: diffFields('', prev.lazyLoading, curr.lazyLoading, LAZY_LOADING_FIELDS) },
    gamConfig: { changes: diffFields('', prev.gamConfig, curr.gamConfig, GAM_CONFIG_FIELDS) },
  }
}

export const useAdmStackChangelog = (output1: AdmStackOutput | undefined, output2: AdmStackOutput | undefined) => {
  const changelog = useMemo<AdmChangelog>(() => {
    return {
      desktop: computeDeviceChangelog(output1?.desktop, output2?.desktop),
      mobile: computeDeviceChangelog(output1?.mobile, output2?.mobile),
    }
  }, [output1, output2])

  return { changelog }
}
